import type { AdminPermissionString } from '@/types/admin'
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { ADMIN_ROLE_PERMISSIONS } from '@/config/admin/rbac'
import { useUserStorev2 as useUserStore } from '@/store/modules/userCenter'
import { AdminRbacRole } from '@/types/admin'

export const useAdminRbacStore = defineStore('admin-rbac-v2', () => {
  const roles = ref<AdminRbacRole[]>([])
  const permissions = ref<AdminPermissionString[]>([])
  const loaded = ref(false)

  const userStore = useUserStore()

  const reset = () => {
    roles.value = []
    permissions.value = []
    loaded.value = false
    localStorage.removeItem('admin-rbac-roles')
  }

  const computePermissionsForRoles = (roleList: AdminRbacRole[], extra: string[] = []): string[] => {
    const fromRoles = roleList.flatMap(role => ADMIN_ROLE_PERMISSIONS[role] ?? [])
    // 过滤掉非字符串或 undefined 的项，确保安全性
    const adminExtras = extra.filter(p => typeof p === 'string' && p.startsWith('admin:'))
    return Array.from(new Set<string>([...fromRoles, ...adminExtras]))
  }

  /**
   * 从登录响应初始化RBAC权限
   * @param adminRoles 管理员角色列表
   * @param extraPermissions 额外的权限列表
   */
  const initializeFromLogin = (adminRoles: AdminRbacRole[], extraPermissions: string[] = []) => {
    if (adminRoles?.length) {
      roles.value = adminRoles
      permissions.value = computePermissionsForRoles(adminRoles, extraPermissions)

      // 持久化角色到 localStorage
      try {
        localStorage.setItem('admin-rbac-roles', JSON.stringify(adminRoles))
      }
      catch {
        // ignore errors from localStorage
      }
    }
    else {
      // 注意: 此处 Mock 回退逻辑是 dev:mock 模式独立开发时的必要功能。\n      // 在 dev:api-local 或生产环境中，必须严格依赖后端真实的 RBAC 角色返回。
      const isMockMode = import.meta.env.VITE_USE_MOCK_DATA === 'true'
      if (isMockMode) {
        if (import.meta.env.VITE_APP_API_LOG === 'true') {
          console.warn('[Admin RBAC] Mock 模式: 未提供管理员角色，默认设置为超级管理员')
        }
        roles.value = [AdminRbacRole.SUPER_ADMIN]
        permissions.value = computePermissionsForRoles(roles.value, extraPermissions)
      }
      else {
        if (import.meta.env.VITE_APP_API_LOG === 'true') {
          console.error('[Admin RBAC] 生产环境: 未提供管理员角色，权限将为空')
        }
        roles.value = []
        permissions.value = []
      }
    }
    loaded.value = true
  }

  /**
   * 从用户Store加载当前管理员RBAC权限
   */
  const loadCurrentAdminRbac = async () => {
    if (!userStore.isLoggedIn) {
      reset()
      loaded.value = true
      return { roles: [], permissions: [] }
    }

    const extraAdminPerms = Array.isArray(userStore.permissions)
      ? userStore.permissions.filter(p => typeof p === 'string' && p.startsWith('admin:'))
      : []

    // 尝试从 localStorage 恢复角色
    if (roles.value.length === 0) {
      try {
        const storedRoles = localStorage.getItem('admin-rbac-roles')
        if (storedRoles) {
          const parsedRoles = JSON.parse(storedRoles)
          if (Array.isArray(parsedRoles) && parsedRoles.length > 0) {
            roles.value = parsedRoles
          }
        }
      }
      catch {
        // ignore errors from localStorage
      }
    }

    // 注意: 此处 Mock 回退逻辑是 dev:mock 模式独立开发时的必要功能。\n    // 仅在 Mock 模式下且角色仍为空时，默认设置为超级管理员角色，其他环境严格限制。
    if (roles.value.length === 0) {
      const isMockMode = import.meta.env.VITE_USE_MOCK_DATA === 'true'
      if (isMockMode) {
        roles.value = [AdminRbacRole.SUPER_ADMIN]
      }
      else {
        if (import.meta.env.VITE_APP_API_LOG === 'true') {
          console.warn('[Admin RBAC] 生产环境: 角色为空，权限将为空')
        }
      }
    }

    permissions.value = computePermissionsForRoles(roles.value, extraAdminPerms)
    loaded.value = true

    return { roles: roles.value, permissions: permissions.value }
  }

  const hasRole = (role: AdminRbacRole): boolean => roles.value.includes(role)

  const hasAnyRole = (targetRoles: AdminRbacRole[]): boolean =>
    targetRoles.some(role => roles.value.includes(role))

  const hasPermission = (permission: string): boolean =>
    permissions.value.includes(permission) || permissions.value.includes('admin:*')

  const hasAnyPermission = (perms: string[]): boolean =>
    perms.some(p => hasPermission(p))

  const hasAllPermissions = (perms: string[]): boolean =>
    perms.every(p => hasPermission(p))

  return {
    // state
    roles,
    permissions,
    loaded,
    // actions
    loadCurrentAdminRbac,
    initializeFromLogin,
    reset,
    // helpers
    hasRole,
    hasAnyRole,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  }
})

export type AdminRbacStore = ReturnType<typeof useAdminRbacStore>
